import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Title,
  Stack,
  Group,
  Button,
  Table,
  Badge,
  ActionIcon,
  Tooltip,
  Modal,
  Select,
  NumberInput,
  Textarea,
  Text,
  Paper,
  Divider,
  Container,
  Box,
  Center,
  Loader,
  Checkbox,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useForm } from "@mantine/form";
import { IconEdit, IconTrash, IconPlus } from "@tabler/icons-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import {
  labPaymentAgreementsService,
  type LabPaymentAgreement,
  type CalculationBasis,
  type CreateAgreementDto,
} from "../../../services/labPaymentAgreements.service";
import { usePractices } from "../../../hooks/admin/usePractices";
import { cptCodesService } from "../../../services/admin/cptcodes.service";
import { required, scrollToFirstError } from "../../../utils/formValidation";

const BASIS_OPTIONS = [
  { value: "cpt_code", label: "Per CPT Code" },
  { value: "block", label: "Per Block" },
  { value: "slide", label: "Per Slide" },
];

export default function LabPaymentAgreementsAdminPage() {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [agreementToDeactivate, setAgreementToDeactivate] =
    useState<LabPaymentAgreement | null>(null);
  const [confirmOpen, { open: openConfirm, close: closeConfirm }] =
    useDisclosure(false);

  const [cptRates, setCptRates] = useState<
    Record<string, { included: boolean; rate: number | ""; id?: string }>
  >({});
  const [isSavingRates, setIsSavingRates] = useState(false);

  const form = useForm({
    initialValues: {
      practiceId: "",
      calculationBasis: "cpt_code" as CalculationBasis,
      flatRate: "" as number | "",
      notes: "",
    },
    validate: {
      practiceId: required("Practice is required"),
    },
  });

  const { data: cptCodes = [], isLoading: isLoadingCptCodes } = useQuery({
    queryKey: ["cpt-codes"],
    queryFn: () => cptCodesService.getAll(),
  });

  const { data: agreementRates = [], isLoading: isLoadingRates } = useQuery({
    queryKey: ["agreement-rates", editingId],
    queryFn: () => labPaymentAgreementsService.listRates(editingId!),
    enabled: !!editingId && form.values.calculationBasis === "cpt_code",
  });

  useEffect(() => {
    if (modalOpen) {
      const initial: Record<
        string,
        { included: boolean; rate: number | ""; id?: string }
      > = {};

      cptCodes.forEach((code) => {
        initial[code.id] = { included: false, rate: "" };
      });

      if (editingId && agreementRates && agreementRates.length > 0) {
        agreementRates.forEach((r) => {
          initial[r.cptCodeId] = {
            included: true,
            rate: r.rate,
            id: r.id,
          };
        });
      }

      setCptRates(initial);
    }
  }, [modalOpen, editingId, cptCodes, agreementRates]);

  const handleClose = useCallback(() => {
    setModalOpen(false);
    setEditingId(null);
  }, []);

  const { data: agreements = [], isLoading } = useQuery({
    queryKey: ["lab-payment-agreements"],
    queryFn: () => labPaymentAgreementsService.listAgreements(),
  });

  const { data: practices = [], isLoading: isLoadingPractices } =
    usePractices();

  const practiceOptions = useMemo(
    () =>
      [...practices]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((p) => ({ value: p.id, label: p.name })),
    [practices]
  );

  const createMutation = useMutation({
    mutationFn: (dto: CreateAgreementDto) =>
      labPaymentAgreementsService.createAgreement(dto),
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      dto,
    }: {
      id: string;
      dto: Partial<CreateAgreementDto>;
    }) => labPaymentAgreementsService.updateAgreement(id, dto),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => labPaymentAgreementsService.deleteAgreement(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lab-payment-agreements"] });
      notifications.show({
        title: "Deactivated",
        message: "Agreement deactivated.",
        color: "orange",
      });
    },
    onError: (err: any) => {
      notifications.show({
        title: "Error",
        message: err?.response?.data?.error ?? "Failed to deactivate.",
        color: "red",
      });
    },
  });

  const openCreate = useCallback(() => {
    setEditingId(null);
    form.reset();
    setModalOpen(true);
  }, [form]);

  const openEdit = useCallback(
    (agreement: LabPaymentAgreement) => {
      setEditingId(agreement.id);
      form.reset();
      form.setValues({
        practiceId: agreement.practiceId,
        calculationBasis: agreement.calculationBasis,
        flatRate: agreement.flatRate != null ? Number(agreement.flatRate) : "",
        notes: agreement.notes ?? "",
      });
      setModalOpen(true);
    },
    [form]
  );

  const handleSubmit = form.onSubmit(
    async (values) => {
      if (values.calculationBasis === "cpt_code") {
        let hasValidationError = false;
        let errorMessage = "";

        Object.entries(cptRates).forEach(([cptId, val]) => {
          if (val.included) {
            if (
              val.rate === "" ||
              typeof val.rate !== "number" ||
              isNaN(val.rate) ||
              val.rate < 0
            ) {
              hasValidationError = true;
              const cptCodeObj = cptCodes.find((c) => c.id === cptId);
              errorMessage = `CPT code ${
                cptCodeObj?.code ?? ""
              } requires a valid non-negative rate.`;
            }
          }
        });

        if (hasValidationError) {
          notifications.show({
            title: "Validation Error",
            message: errorMessage,
            color: "red",
          });
          return;
        }
      }

      const dto: CreateAgreementDto = {
        practiceId: values.practiceId,
        calculationBasis: values.calculationBasis,
        flatRate: values.flatRate !== "" ? Number(values.flatRate) : null,
        notes: values.notes || null,
      };

      try {
        setIsSavingRates(true);
        let agreementId = editingId;
        if (editingId) {
          await updateMutation.mutateAsync({ id: editingId, dto });
        } else {
          const newAgreement = await createMutation.mutateAsync(dto);
          agreementId = newAgreement.id;
        }

        if (values.calculationBasis === "cpt_code" && agreementId) {
          const promises: Promise<any>[] = [];

          Object.entries(cptRates).forEach(([cptId, val]) => {
            const numericRate = typeof val.rate === "number" ? val.rate : 0;
            if (val.included) {
              if (val.id) {
                const existingRateObj = agreementRates.find(
                  (r) => r.id === val.id
                );
                if (!existingRateObj || existingRateObj.rate !== numericRate) {
                  promises.push(
                    labPaymentAgreementsService.updateRate(
                      agreementId!,
                      val.id,
                      {
                        rate: numericRate,
                      }
                    )
                  );
                }
              } else {
                promises.push(
                  labPaymentAgreementsService.createRate(agreementId!, {
                    cptCodeId: cptId,
                    rate: numericRate,
                  })
                );
              }
            } else if (val.id) {
              promises.push(
                labPaymentAgreementsService.deleteRate(agreementId!, val.id)
              );
            }
          });

          if (promises.length > 0) {
            await Promise.all(promises);
          }
        }

        queryClient.invalidateQueries({ queryKey: ["lab-payment-agreements"] });
        queryClient.invalidateQueries({
          queryKey: ["agreement-rates", agreementId],
        });

        handleClose();
        notifications.show({
          title: "Saved",
          message: editingId ? "Agreement updated." : "Agreement created.",
          color: "green",
        });
      } catch (err: any) {
        notifications.show({
          title: "Error",
          message:
            err?.response?.data?.error ??
            err?.message ??
            "Failed to save agreement.",
          color: "red",
        });
      } finally {
        setIsSavingRates(false);
      }
    },
    () => scrollToFirstError(form)
  );

  const isSaving =
    createMutation.isPending || updateMutation.isPending || isSavingRates;

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
        <Stack gap="lg">
          <Group justify="space-between">
            <Title order={2}>Lab Payment Agreements</Title>
            <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
              Add Agreement
            </Button>
          </Group>

          <Paper withBorder>
            {isLoading ? (
              <Center p="xl">
                <Loader />
              </Center>
            ) : agreements.length === 0 ? (
              <Text p="md" c="dimmed">
                No agreements configured yet.
              </Text>
            ) : (
              <Table striped highlightOnHover withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Practice</Table.Th>
                    <Table.Th>Basis</Table.Th>
                    <Table.Th>Rate</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th w={80} />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {agreements.map((a) => (
                    <Table.Tr key={a.id}>
                      <Table.Td>{a.practice?.name ?? a.practiceId}</Table.Td>
                      <Table.Td>
                        <Badge variant="light">
                          {BASIS_OPTIONS.find(
                            (o) => o.value === a.calculationBasis
                          )?.label ?? a.calculationBasis}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        {a.calculationBasis === "cpt_code"
                          ? "Rate table"
                          : a.flatRate != null
                          ? `$${Number(a.flatRate).toFixed(2)}`
                          : "—"}
                      </Table.Td>
                      <Table.Td>
                        <Badge
                          color={a.isActive ? "green" : "gray"}
                          variant="light"
                        >
                          {a.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Group gap={4}>
                          <Tooltip label="Edit">
                            <ActionIcon
                              size="sm"
                              variant="subtle"
                              onClick={() => openEdit(a)}
                            >
                              <IconEdit size={14} />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip label="Deactivate">
                            <ActionIcon
                              size="sm"
                              variant="subtle"
                              color="red"
                              onClick={() => {
                                setAgreementToDeactivate(a);
                                openConfirm();
                              }}
                            >
                              <IconTrash size={14} />
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Paper>

          <Modal.Root
            opened={modalOpen}
            onClose={handleClose}
            size="md"
            closeOnClickOutside={false}
            styles={{
              content: {
                display: "flex",
                flexDirection: "column",
                maxHeight: "85vh",
              },
            }}
          >
            <Modal.Overlay />
            <Modal.Content>
              <Modal.Header>
                <Modal.Title>
                  {editingId ? "Edit Agreement" : "Add Agreement"}
                </Modal.Title>
                <Modal.CloseButton />
              </Modal.Header>
              <Modal.Body style={{ flex: 1, overflow: "auto" }}>
                <form id="agreement-form" onSubmit={handleSubmit}>
                  <Stack gap="md">
                    <Select
                      label="Practice"
                      placeholder={
                        isLoadingPractices
                          ? "Loading practices…"
                          : "Select a practice"
                      }
                      data={practiceOptions}
                      searchable
                      nothingFoundMessage="No practices found"
                      disabled={isLoadingPractices}
                      withAsterisk
                      {...form.getInputProps("practiceId")}
                    />
                    <Select
                      label="Calculation Basis"
                      data={BASIS_OPTIONS}
                      value={form.values.calculationBasis}
                      onChange={(v) =>
                        form.setFieldValue(
                          "calculationBasis",
                          v as CalculationBasis
                        )
                      }
                    />
                    {form.values.calculationBasis !== "cpt_code" && (
                      <NumberInput
                        label="Flat Rate (per unit)"
                        placeholder="0.00"
                        prefix="$"
                        decimalScale={2}
                        value={form.values.flatRate}
                        onChange={(v) =>
                          form.setFieldValue(
                            "flatRate",
                            typeof v === "number" ? v : ""
                          )
                        }
                      />
                    )}
                    {form.values.calculationBasis === "cpt_code" && (
                      <>
                        <Divider label="CPT Code Rates" labelPosition="left" />
                        {isLoadingCptCodes || (editingId && isLoadingRates) ? (
                          <Center p="md">
                            <Loader size="sm" />
                          </Center>
                        ) : cptCodes.length === 0 ? (
                          <Text size="xs" c="dimmed">
                            No CPT codes found in the system.
                          </Text>
                        ) : (
                          <Table verticalSpacing="xs">
                            <Table.Thead>
                              <Table.Tr>
                                <Table.Th style={{ width: 80 }}>Count</Table.Th>
                                <Table.Th>CPT Code</Table.Th>
                                <Table.Th style={{ width: 150 }}>
                                  Rate ($)
                                </Table.Th>
                              </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                              {cptCodes
                                .filter(
                                  (c) => c.isActive || cptRates[c.id]?.included
                                )
                                .sort((a, b) => a.code.localeCompare(b.code))
                                .map((code) => {
                                  const state = cptRates[code.id] || {
                                    included: false,
                                    rate: "",
                                  };
                                  return (
                                    <Table.Tr key={code.id}>
                                      <Table.Td>
                                        <Checkbox
                                          checked={state.included}
                                          onChange={(event) => {
                                            setCptRates((prev) => ({
                                              ...prev,
                                              [code.id]: {
                                                ...state,
                                                included:
                                                  event.currentTarget.checked,
                                                rate:
                                                  event.currentTarget.checked &&
                                                  state.rate === ""
                                                    ? 0
                                                    : state.rate,
                                              },
                                            }));
                                          }}
                                        />
                                      </Table.Td>
                                      <Table.Td>
                                        <Text size="sm" fw={500}>
                                          {code.code}
                                        </Text>
                                        {code.description && (
                                          <Text
                                            size="xs"
                                            c="dimmed"
                                            lineClamp={1}
                                          >
                                            {code.description}
                                          </Text>
                                        )}
                                      </Table.Td>
                                      <Table.Td>
                                        <NumberInput
                                          disabled={!state.included}
                                          value={state.rate}
                                          onChange={(val) => {
                                            setCptRates((prev) => ({
                                              ...prev,
                                              [code.id]: {
                                                ...state,
                                                rate:
                                                  val === "" ? "" : Number(val),
                                              },
                                            }));
                                          }}
                                          min={0}
                                          decimalScale={2}
                                          placeholder="0.00"
                                        />
                                      </Table.Td>
                                    </Table.Tr>
                                  );
                                })}
                            </Table.Tbody>
                          </Table>
                        )}
                      </>
                    )}
                    <Textarea
                      label="Notes"
                      placeholder="Internal notes about this agreement"
                      value={form.values.notes}
                      onChange={(e) =>
                        form.setFieldValue("notes", e.target.value)
                      }
                      rows={2}
                    />
                  </Stack>
                </form>
              </Modal.Body>
              <Divider />
              <Group justify="space-between" p="md">
                <Button
                  variant="default"
                  onClick={handleClose}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
                <Button type="submit" form="agreement-form" loading={isSaving}>
                  Save
                </Button>
              </Group>
            </Modal.Content>
          </Modal.Root>

          <Modal
            opened={confirmOpen}
            onClose={closeConfirm}
            title="Deactivate Agreement"
            size="sm"
            centered
          >
            <Stack gap="md">
              <Text>
                Are you sure you want to deactivate the agreement for{" "}
                <strong>
                  {agreementToDeactivate?.practice?.name ??
                    agreementToDeactivate?.practiceId}
                </strong>
                ?
              </Text>
              <Text size="sm" c="dimmed">
                Deactivated agreements will no longer be used for payables
                calculations.
              </Text>
              <Group justify="flex-end" mt="md">
                <Button variant="subtle" onClick={closeConfirm}>
                  Cancel
                </Button>
                <Button
                  color="red"
                  loading={deleteMutation.isPending}
                  onClick={() => {
                    if (agreementToDeactivate) {
                      deleteMutation.mutate(agreementToDeactivate.id);
                      closeConfirm();
                    }
                  }}
                >
                  Deactivate
                </Button>
              </Group>
            </Stack>
          </Modal>
        </Stack>
      </Box>
    </Container>
  );
}
